/**
 * Part Timer: a stopwatch for calibrating Smart Auto Scroll.
 *
 * Smart Auto Scroll guesses how long a part lasts from bars x seconds-per-bar (songTiming.js). That
 * guess is the one part of the schedule with nothing to check it against — every other input is
 * measured. This records the ground truth: the player taps once at each part boundary while
 * actually playing the song, and every lap is stored next to what the algorithm predicted for that
 * same part, so the two can be diffed afterwards.
 *
 * It is a calibration instrument, not a feature — off by default, and switched on from the Auto
 * Scroll settings modal. Sessions accumulate in localStorage across songs so a whole set can be
 * played through before the data is exported in one go.
 */

import { estimatePartBars, estimatePartDuration, getBeatsPerBar, getSecondsPerBar, getTimedLines } from './songTiming';
import { chartBars } from './partCharts';
import { isChordLine, isLyricsLine, parseRepeatDirective } from './repeat';
import { isSectionPart } from './partNumbers';
import { getSchedule, measureRenderedParts } from './smartScrollSchedule';
import { getSetting, setSetting, settingKeys } from './settings';
import { READING_FRACTION, SPEED_LEVELS, speedToPxPerSecond } from './autoScrollConstants';
import { readItem, storageKeys, writeItem, removeItem } from './storage';
import authService from '../services/authService';

/** Fired by the settings modal so an open View picks the toggle up without a remount. */
export const TIMING_RECORDER_CHANGED_EVENT = 'timingRecorderChanged';

/**
 * Whether the Part Timer exists at all in this build.
 *
 * It is an instrument for calibrating the estimator, not a feature anyone performing from the app
 * should meet — so it is kept out of production entirely rather than merely defaulted off. The
 * check is a compile-time constant in a CRA build, which lets the bundler drop the overlay, this
 * module and the settings row from the production bundle as dead code.
 *
 * It stays in the tree rather than being deleted because calibration recurs: the estimator will be
 * revisited as songs with other shapes turn up, and re-recording is worth far more than the few
 * kilobytes it costs a development build. Gate any future recording UI on this same flag.
 *
 * To run another calibration round, flip RECORDING_ROUND_IN_PROGRESS to true and restart the dev
 * server. That is the whole switch — both toggles (the Part Timer and the Smart Scroll X-Ray)
 * reappear in the Auto Scroll settings modal, and any runs still held in localStorage from the
 * previous round come back with it. Leave it false the rest of the time so a development build
 * looks like the real app.
 */
const RECORDING_ROUND_IN_PROGRESS = false;

export const TIMING_RECORDER_AVAILABLE = RECORDING_ROUND_IN_PROGRESS && process.env.NODE_ENV === 'development';

/**
 * The belt to the build flag's braces: even in a build where the instruments exist, only an admin
 * may see one.
 *
 * TIMING_RECORDER_AVAILABLE is the real guard and a deploy cannot carry these past it — the
 * constant is false in production, so the bundler drops the code. This is the guard for the day
 * that reasoning is wrong: a development build served somewhere real, NODE_ENV set by accident, a
 * future refactor that makes the flag dynamic. A player who is handed a stopwatch overlay across
 * their lyrics mid-service has lost the song, and none of the three ways that could happen are
 * things they should have to survive.
 *
 * Checked live rather than folded into the constant above, because the user arrives after this
 * module is first imported — a value captured at import time would be false for the whole session.
 */
export const isCalibrationUser = () =>
  TIMING_RECORDER_AVAILABLE && Boolean(authService.getCurrentUser()?.isAdmin);

export const isTimingRecorderEnabled = () =>
  isCalibrationUser() && Boolean(getSetting(settingKeys.timingRecorder));

/* ---------------------------------------------------------------------------
 * The plan: what will be timed, and what the algorithm expects of it
 * ------------------------------------------------------------------------- */

/**
 * The raw inputs the estimate was made from, and which of its branches produced it.
 *
 * estimatePartDuration returns a number; calibration needs to know how that number was arrived at.
 * A part timed from a MIDI clip being 30% short is a different bug from a chords-only Intro being
 * 30% short, and averaged together they cancel out into a shrug. `source` keeps them apart.
 *
 * This deliberately re-walks the branches of songTiming.js rather than having that module report
 * them. The estimator is the thing under test; an instrument that reaches into it to ask what it
 * did would be one more place to keep in step, for a tool that gets deleted once calibration ends.
 * If the two ever disagree, trust songTiming — and the disagreement is itself worth knowing.
 *
 * chartBars is shared rather than re-walked, because reading a chart's length is notation-reading
 * and not estimating: there is no branch of the algorithm in it to be checked against.
 *
 * Exported because the x-ray overlay (smartScrollDebug.js) reports the same branch on screen: one
 * description of what the estimator did, read by both instruments.
 */
export const describeEstimate = (part, beatsPerBar = 4) => {
  const { count, duration } = parseRepeatDirective(part?.lyrics || '');
  const { strippedLyrics, lines } = getTimedLines(part, beatsPerBar);
  const lyricLines = lines.filter(isLyricsLine).length;
  const chordLines = lines.filter(isChordLine).length;

  let source;
  if (isSectionPart(part)) source = 'section';
  else if (chartBars(part, strippedLyrics, beatsPerBar)) source = 'chart'; // the part's own chart, in bars
  else if (count && duration) source = 'directive'; // {Nx,seconds} — a stopwatched pass
  else if (lyricLines > 0) source = 'lyrics';
  else if (chordLines > 0) source = 'chords';
  else source = 'default';

  return { source, lyricLines, chordLines, barsPerPass: estimatePartBars(part, beatsPerBar) };
};

/**
 * The parts to walk through, in the order they are on screen, each carrying the prediction it is
 * about to be judged against.
 *
 * The list comes from measureRenderedParts rather than from song.parts so it is exactly the list
 * the scheduler works from — same overview filtering, same attached-note filtering, same
 * continuous-view ordering. A lap can then be lined up with a breakpoint by index.
 */
export const buildPartPlan = (songs = []) => {
  const measured = measureRenderedParts();
  if (!measured.length) return [];

  const byId = {};
  songs.forEach((song) => {
    if (!song?._id) return;
    const secondsPerBar = getSecondsPerBar(song);
    (song.parts || []).forEach((part) => {
      byId[part.id] = { song, part, secondsPerBar };
    });
  });

  // The schedule only exists while Smart Scroll is on. When it is, its breakpoints are what the
  // scroll actually did, which is a truer comparison than the raw estimate — so record both.
  const schedule = getSchedule();
  const breakpoints = schedule?.breakpoints || [];

  return measured.map((node, index) => {
    const entry = byId[node.partId];
    const part = entry?.part;
    const beatsPerBar = getBeatsPerBar(entry?.song?.timeSignature);
    const duration = part ? estimatePartDuration(part, entry.secondsPerBar, beatsPerBar) : null;

    const from = breakpoints[index];
    const to = breakpoints[index + 1];
    const aligned = from && to && from.partId === node.partId;
    const scheduledSeconds = aligned ? Number((to.time - from.time).toFixed(2)) : null;
    // The pixels the schedule actually allots this part, which is not its height: the anchor is
    // the reading line, so a part's share of the scroll is the gap to the next part's anchor
    const scheduledPx = aligned ? Math.round(to.position - from.position) : null;

    const estimate = part ? describeEstimate(part, beatsPerBar) : {};

    return {
      index,
      partId: node.partId,
      songId: node.songId || entry?.song?._id || null,
      songTitle: entry?.song?.title || null,
      partName: part ? `${part.name} ${part.number ?? ''}`.trim() : node.partId,
      // The bare name is the categorical variable the calibration groups on — "Chorus" across
      // eight songs is the comparison, and "Chorus 2" would split it into singletons
      partType: part?.name || null,
      repeatCount: Number(part?.repeatCount) > 1 ? Number(part.repeatCount) : 1,
      heightPx: Math.round(node.bottom - node.top),
      ...estimate,
      predictedSeconds: duration?.seconds != null ? Number(duration.seconds.toFixed(2)) : null,
      predictedBars: duration?.bars != null ? Number(duration.bars.toFixed(2)) : null,
      exact: Boolean(duration?.exact),
      // Whether the engine ran this part at 1x. Taken off the breakpoint where there is one: the
      // estimator says the chart stated the length, the schedule says whether it honoured that.
      measured: aligned ? Boolean(from.measured) : Boolean(duration?.measured),
      scheduledSeconds,
      scheduledPx,
    };
  });
};

/* ---------------------------------------------------------------------------
 * Run setup
 *
 * A recording run wants the app in one particular state, and setting it up by hand before every
 * song is both slow and easy to get wrong — a run played with Overview off times the duplicates
 * as well as the originals, which is twice the taps for parts whose durations are already known
 * from their originals.
 *
 * So the start button arranges it: Overview on (originals only), the metronome following auto
 * scroll, and the scroll running. Everything is put back when the run ends, because these are the
 * player's own settings and a calibration session should not quietly rewrite them.
 * ------------------------------------------------------------------------- */

/**
 * The settings a run takes over, captured before it changes them. Only the persisted half lives
 * here — Overview and the scroll are React state, so the overlay restores those itself.
 */
export const captureRunSettings = () => ({
  metronomeFollowAutoScroll: getSetting(settingKeys.metronomeFollowAutoScroll),
  overview: getSetting(settingKeys.overviewPlugin),
  smartScrollDebug: getSetting(settingKeys.smartScrollDebug),
});

export const applyRunSettings = () => {
  setSetting(settingKeys.metronomeFollowAutoScroll, true);
  // The x-ray is what makes a run legible while it plays — the part the engine is inside lights up
  // as the page scrolls, so a boundary that arrives early is seen rather than worked out afterwards
  setSetting(settingKeys.smartScrollDebug, true);
};

/**
 * Puts back exactly what was captured, undefined included: a setting the player had never touched
 * should go back to never having been touched, not to an explicit false.
 */
export const restoreRunSettings = (captured) => {
  if (!captured) return;
  setSetting(settingKeys.metronomeFollowAutoScroll, captured.metronomeFollowAutoScroll);
  setSetting(settingKeys.overviewPlugin, captured.overview);
  setSetting(settingKeys.smartScrollDebug, captured.smartScrollDebug);
};

/* ---------------------------------------------------------------------------
 * Sessions
 * ------------------------------------------------------------------------- */

const SESSIONS_KEY = storageKeys.timingSessions;

export const loadSessions = () => {
  try {
    const parsed = JSON.parse(readItem(SESSIONS_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

/**
 * Appends a finished run. A song played twice keeps both takes — a second pass at a different
 * tempo is data, not a correction.
 */
export const saveSession = (session) => {
  const sessions = [...loadSessions(), session];
  writeItem(SESSIONS_KEY, JSON.stringify(sessions));
  return sessions;
};

/**
 * Drops one run. A mistap is only discovered after the song has been played, so the fix has to be
 * per-run — clearing everything to lose one bad take would cost the whole set.
 */
export const deleteSession = (index) => {
  const sessions = loadSessions().filter((_, i) => i !== index);
  writeItem(SESSIONS_KEY, JSON.stringify(sessions));
  return sessions;
};

export const clearSessions = () => {
  removeItem(SESSIONS_KEY);
  return [];
};

/** A song's context, captured once per run so the export is self-contained. */
/**
 * The rendered line height in the song body, so a part's pixel height can be read as a number of
 * lines. A phone wraps a lyric line that a desktop fits on one, and that wrapping is the whole
 * difference between the two layouts — but only if the heights can be divided by something.
 */
const measureLineHeight = () => {
  const body = document.querySelector('#view-body');
  if (!body) return null;

  const { lineHeight, fontSize } = window.getComputedStyle(body);
  const parsed = parseFloat(lineHeight);
  if (Number.isFinite(parsed)) return Math.round(parsed * 100) / 100;

  // 'normal' resolves to roughly 1.2x the font size, which is close enough to divide by
  const size = parseFloat(fontSize);
  return Number.isFinite(size) ? Math.round(size * 1.2 * 100) / 100 : null;
};

export const describeSong = (songs = [], scroll = {}) => {
  const doc = document.documentElement;
  const { scrollingSpeed = null, scrollingPace = null, isScrolling = false } = scroll;
  const level = SPEED_LEVELS.find((entry) => entry.speed === scrollingSpeed);

  return {
    recordedAt: new Date().toISOString(),
    songs: songs.filter(Boolean).map((song) => ({
      id: song._id,
      title: song.title,
      bpm: song.bpm ?? null,
      timeSignature: song.timeSignature ?? null,
      // Recorded rather than re-derived later: it is the constant every bar estimate is multiplied
      // by, so a wrong time signature shows up here instead of hiding inside every row
      secondsPerBar: getSecondsPerBar(song),
    })),
    // Layout is the other half of the schedule — the same song on a phone and on a tablet gets a
    // different number of wrapped lines, so a part's height is only comparable within one of these
    viewport: { width: window.innerWidth, height: window.innerHeight },

    // The height buildAnchors actually works from. Deliberately recorded alongside innerHeight
    // rather than instead of it: on mobile Safari the two differ by the collapsing browser chrome,
    // and it is this one the reading line is a fraction of. Comparing a phone run against a desktop
    // run using innerHeight would put the reading line in the wrong place on the phone.
    clientHeight: doc.clientHeight,
    readingLinePx: Math.round(doc.clientHeight * READING_FRACTION),

    // Heights are in pixels, which are not comparable across devices or font sizes on their own.
    // This is the divisor that turns them into rendered lines, which are.
    lineHeightPx: measureLineHeight(),

    screen: { width: window.screen?.width ?? null, height: window.screen?.height ?? null, dpr: window.devicePixelRatio },
    scrollHeight: doc.scrollHeight,
    maxScroll: doc.scrollHeight - doc.clientHeight,
    fontSize: getSetting(settingKeys.fontSize) || null,
    hideChords: Boolean(getSetting(settingKeys.hideChordsPlugin)),
    overview: Boolean(getSetting(settingKeys.overviewPlugin)),
    smartScroll: Boolean(getSetting(settingKeys.smartScroll)),

    // What the scroll was set to while this run was played.
    //
    // This is not a detail: the pace multiplier is applied by the engine, not baked into the
    // schedule, so the per-part `scheduledSeconds` below is the *unscaled* figure. Without this
    // field a run recorded at 120% reads as a 20% algorithm error that isn't there.
    //
    // It does not divide out uniformly. The engine applies pace only where the schedule had to
    // estimate — a part the chart measured (bar lines, or a {Nx,seconds} directive) runs at 1x
    // however the slider is set. So a part marked exact, and any chords-only part written out in
    // bar lines, was played at its scheduledSeconds; everything else was played at
    // scheduledSeconds / pace. The x-ray's "pace locked" badge is the per-part answer.
    scroll: {
      mode: getSetting(settingKeys.smartScroll) ? 'smart' : 'constant',
      running: isScrolling,
      pace: scrollingPace,
      pacePercent: scrollingPace != null ? Math.round(scrollingPace * 100) : null,
      speed: scrollingSpeed,
      speedLevel: level?.label ?? null,
      constantPxPerSecond: scrollingSpeed ? Number(speedToPxPerSecond(scrollingSpeed).toFixed(2)) : null,
    },
  };
};

/* ---------------------------------------------------------------------------
 * Export
 * ------------------------------------------------------------------------- */

const formatSeconds = (value) => (value == null ? '—' : `${Number(value).toFixed(1)}s`);

/** The scroll settings line, spelled out because the Scheduled column can't be read without it. */
const describeScroll = (scroll) => {
  if (!scroll) return 'Scroll settings: not recorded (run predates this field)';

  const running = scroll.running ? 'running' : 'NOT running';

  if (scroll.mode === 'smart') {
    const pace = scroll.pace ?? 1;
    if (pace === 1) return `Scroll: smart, ${running}, pace 100% — Scheduled column is what was played`;

    return (
      `Scroll: smart, ${running}, pace ${scroll.pacePercent ?? '?'}%` +
      ` — Scheduled column is unscaled. Pace applies only to estimated parts, so divide by ${pace}` +
      ' for the parts NOT measured off the chart (bar lines or a {Nx,seconds} directive);' +
      ' measured parts were played at the Scheduled figure itself'
    );
  }

  return `Scroll: constant, ${running}, speed level ${scroll.speedLevel ?? '?'} (${
    scroll.speed ?? '?'
  }ms/px = ${scroll.constantPxPerSecond ?? '?'} px/s)`;
};

/**
 * The whole recording as text: a table per run, with the measured time beside both predictions and
 * the ratio between them. The ratio is the number the calibration turns on — a column of 1.4s says
 * the estimate is short by a consistent factor, a column that scatters says the shape is wrong.
 */
export const formatSessions = (sessions = []) => {
  if (!sessions.length) return 'No parts recorded yet.';

  return sessions
    .map((session, sessionIndex) => {
      const heading = session.songs
        .map(
          (song) =>
            `${song.title} (BPM ${song.bpm ?? '—'}, ${song.timeSignature || '4/4'}` +
            `${song.secondsPerBar ? `, ${song.secondsPerBar.toFixed(2)}s/bar` : ''})`,
        )
        .join(' + ');

      const total = session.laps.reduce((sum, lap) => sum + lap.seconds, 0);
      const predictedTotal = session.laps.reduce((sum, lap) => sum + (lap.predictedSeconds || 0), 0);

      const rows = session.laps.map((lap) => {
        const ratio = lap.predictedSeconds ? (lap.seconds / lap.predictedSeconds).toFixed(2) : '—';
        return (
          // "locked" marks a part the engine ran at 1x, so its Scheduled figure needs no dividing
          `| ${lap.partName} | ${lap.source || '—'}${lap.measured ? ' (locked)' : ''}` +
          ` | ${lap.lyricLines ?? '—'} | ${lap.chordLines ?? '—'}` +
          ` | ${lap.barsPerPass ?? '—'} | ${lap.repeatCount} | ${lap.heightPx} | ${
            session.lineHeightPx ? (lap.heightPx / session.lineHeightPx).toFixed(1) : '—'
          } | ${lap.scheduledPx ?? '—'}` +
          ` | ${lap.seconds.toFixed(1)} | ${formatSeconds(lap.predictedSeconds)}` +
          ` | ${formatSeconds(lap.scheduledSeconds)} | ${ratio} |`
        );
      });

      return [
        `### Run ${sessionIndex + 1}: ${heading}`,
        `Recorded ${session.recordedAt}${session.partial ? ' (PARTIAL — run ended by a song change)' : ''}`,
        `Viewport ${session.viewport.width}x${session.viewport.height} (client height ${
          session.clientHeight ?? '?'
        }, reading line ${session.readingLinePx ?? '?'}px) · screen ${session.screen?.width ?? '?'}x${
          session.screen?.height ?? '?'
        } @${session.screen?.dpr ?? '?'}x`,
        `Scrollable ${session.maxScroll ?? '?'}px of ${session.scrollHeight ?? '?'}px · line height ${
          session.lineHeightPx ?? '?'
        }px`,
        `Font ${session.fontSize || 'default'} · hide chords ${session.hideChords ? 'on' : 'off'} · overview ${
          session.overview ? 'on' : 'off'
        } · smart scroll ${session.smartScroll ? 'on' : 'off'}`,
        describeScroll(session.scroll),
        `Actual total ${total.toFixed(1)}s · predicted total ${predictedTotal.toFixed(1)}s`,
        '',
        '| Part | Est from | Lyric ln | Chord ln | Bars/pass | Rpt | Height px | Rendered ln | Sched px | Actual | Predicted | Scheduled | Actual/Predicted |',
        '| --- | --- | --- | --- | --- | --- | --- | --- | --- | --- | --- | --- | --- |',
        ...rows,
      ].join('\n');
    })
    .join('\n\n');
};

/** The same data unabridged, for when the table has rounded away something that matters. */
export const exportJson = (sessions = []) => JSON.stringify(sessions, null, 2);
